import Plivo from 'plivo-browser-sdk'
import { queryClient } from 'v2/configs/rq-setup'
import { conversationQueryKeys, getSettingsQueryKeys } from 'v2/shared/utils/query-key-management'
import { usePhoneStore } from 'v2/state/phone'
import type { RUserProfile } from 'v2/types/api/settings'
import type { PlivoConfig } from 'v2/types/call'
import { PrimaryEvents, primaryEventBus } from '../event-bus/primary'
import { phoneLogger } from '../logger'
import { CallManager } from './managers/call-manager'
import { ConnectionManager } from './managers/connection-manager'
import { EventManager } from './managers/event-manager'
import { MediaManager } from './managers/media-manager'

export class PhoneSDK {
  private static instance: PhoneSDK

  private sdk: Plivo | null = null

  // Managers
  private callManager: CallManager

  private mediaManager: MediaManager

  private connectionManager: ConnectionManager

  private eventManager: EventManager

  private constructor() {
    // Initialize managers
    this.callManager = new CallManager()
    this.mediaManager = new MediaManager()
    this.connectionManager = new ConnectionManager()
    this.eventManager = new EventManager(this.connectionManager)

    // Bind methods to maintain context
    this.initialize = this.initialize.bind(this)
    this.connect = this.connect.bind(this)
    this.destroy = this.destroy.bind(this)
    this.isConnected = this.isConnected.bind(this)
    this.isConnecting = this.isConnecting.bind(this)
    this.isLoggedIn = this.isLoggedIn.bind(this)
    this.makeCall = this.makeCall.bind(this)
    this.answerCall = this.answerCall.bind(this)
    this.hangup = this.hangup.bind(this)
    this.rejectCall = this.rejectCall.bind(this)
    this.redirect = this.redirect.bind(this)
    this.isCallActive = this.isCallActive.bind(this)
    this.toggleMute = this.toggleMute.bind(this)
    this.getUsername = this.getUsername.bind(this)
    this.getSdk = this.getSdk.bind(this)

    // Invalidate conversations query on RQ_HYDRATED event if it was set in previous session
    // To handle page reloads during active call
    primaryEventBus.once(PrimaryEvents.RQ_HYDRATED, () => {
      if (localStorage.getItem('INVALIDATE_CONVERSATIONS')) {
        usePhoneStore.getState().reset()
        queryClient.invalidateQueries({ queryKey: conversationQueryKeys().active })
        localStorage.removeItem('INVALIDATE_CONVERSATIONS')
      }
    })
  }

  static getInstance(): PhoneSDK {
    if (!PhoneSDK.instance) PhoneSDK.instance = new PhoneSDK()
    return PhoneSDK.instance
  }

  getUsername(): string {
    return this.connectionManager.getUsername()
  }

  getSdk(): Plivo | null {
    return this.sdk
  }

  async initialize(
    config: PlivoConfig,
    audioConstraints?: MediaTrackConstraints,
    usePlivoStunServer?: boolean,
  ): Promise<void> {
    phoneLogger.local('Initializing Plivo SDK with audio constraints', audioConstraints)

    if (this.sdk) {
      phoneLogger.info('SDK already initialized')
      return
    }

    const enableNoiseReduction = queryClient.getQueryData<RUserProfile>(
      getSettingsQueryKeys().profile,
    )?.organizations[0].allow_noise_reduction

    this.sdk = new Plivo({
      permOnClick: true,
      stopAutoRegisterOnConnect: true,
      captureSDKCrashOnly: true,
      debug: 'DEBUG',
      enableNoiseReduction,
      audioConstraints,
      usePlivoStunServer,
    })

    // Inject SDK instance into all managers
    this.callManager.sdk = this.sdk
    this.mediaManager.sdk = this.sdk
    this.connectionManager.sdk = this.sdk
    this.eventManager.sdk = this.sdk

    // Initialize managers with config
    await this.connectionManager.initialize(config)
    this.eventManager.setupEventListeners()
  }

  // Public API methods that delegate to appropriate managers
  connect(headers: string[]): void {
    phoneLogger.log('invoking connect method')
    this.connectionManager.connect(headers)
  }

  isConnected(): boolean {
    return this.connectionManager.isConnected()
  }

  isConnecting(): boolean {
    return this.connectionManager.isConnecting()
  }

  isLoggedIn(): boolean {
    return this.connectionManager.isLoggedIn()
  }

  makeCall(params: {
    callWith: string
    contactName?: string
    callerId: { name: string; number?: string }
    parentConversationNumber?: string
    crmId?: string
    flowType?: string
    aomUUID?: string
  }): Promise<void> {
    return this.callManager.makeCall(params)
  }

  answerCall(): Promise<void> {
    return this.callManager.answerCall()
  }

  startNoiseReduction(): Promise<boolean> {
    return this.sdk?.client.startNoiseReduction() ?? Promise.resolve(false)
  }

  stopNoiseReduction(): Promise<boolean> {
    return this.sdk?.client.stopNoiseReduction() ?? Promise.resolve(false)
  }

  hangup(): Promise<void> {
    return this.callManager.hangup()
  }

  rejectCall(): Promise<void> {
    return this.callManager.rejectCall()
  }

  redirect(toUri: string): Promise<void> {
    return this.callManager.redirect(toUri)
  }

  isCallActive(): boolean {
    return this.callManager.isCallActive()
  }

  toggleMute(): Promise<void> {
    return this.mediaManager.toggleMute()
  }

  sendDTMF(dtmf: string): Promise<void> {
    return this.mediaManager.sendDTMF(dtmf)
  }

  async destroy(): Promise<void> {
    phoneLogger.info('Destroying Plivo SDK instance')

    // Clean up all managers
    this.eventManager.removeAllListeners()
    await this.connectionManager.destroy()

    // Clear SDK instance
    this.sdk = null

    // Clear SDK reference from all managers
    this.callManager.sdk = null
    this.mediaManager.sdk = null
    this.connectionManager.sdk = null
    this.eventManager.sdk = null

    phoneLogger.info('SDK destroyed successfully')
  }
}

export const phoneSDK = PhoneSDK.getInstance()

/**
 * Phone SDK and Store for handling Plivo WebRTC calls
 *
 * Call Flow:
 * ----------
 * Outbound Call:
 * 1. makeCall() → setActiveCall(callDetails) // Call initiated
 * 2. onCallAnswered → setCallActive(true)    // Remote party answered
 * 3. onCallTerminated → setActiveCall(null) + setCallActive(false) // Call ended
 *
 * Inbound Call:
 * 1. onIncomingCall → setActiveCall(callDetails) // Call received
 * 2. answerCall() → client.answer()              // User answers
 * 3. onCallAnswered → setCallActive(true)        // Call connected
 * 4. onCallTerminated → setActiveCall(null) + setCallActive(false) // Call ended
 *
 * Key Events:
 * -----------
 * - onIncomingCall: New call received
 * - onCallAnswered: Call connected (either party)
 * - onCallTerminated: Call ended
 * - onCallFailed: Call failed to connect
 * - onIncomingCallCanceled: Incoming call was canceled
 */
